import { BaseNumber, bigNumberify, NormalizedNumber, Percentage, promiseAll, RAY } from '@sparkdotfi/common-universal'
import { QueryKey, queryOptions } from '@tanstack/react-query'
import { Config } from 'wagmi'
import { readContract } from 'wagmi/actions'

import {
  lockstakeCappedOsmWrapperConfig,
  lockstakeClipperConfig,
  lockstakeEngineConfig,
  spotterConfig,
} from '@/config/contracts-generated'
import { getContractAddress } from '@/domain/hooks/useContractAddress'

export interface StusdsRiskParametersQueryOptionsParams {
  chainId: number
  wagmiConfig: Config
}

export interface StusdsRiskParameters {
  lockstakeLtv: Percentage
  skyMaxPriceCap: NormalizedNumber
  liquidationEnabled: boolean
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function stusdsRiskParametersQueryOptions({ wagmiConfig, chainId }: StusdsRiskParametersQueryOptionsParams) {
  return queryOptions({
    queryKey: stusdsRiskParametersQueryKey({ chainId }),
    queryFn: () => stusdsRiskParametersQueryFunction({ wagmiConfig, chainId }),
  })
}

export function stusdsRiskParametersQueryKey({ chainId }: { chainId: number }): QueryKey {
  return ['stusds-risk-parameters', chainId]
}

export async function stusdsRiskParametersQueryFunction({
  wagmiConfig,
  chainId,
}: StusdsRiskParametersQueryOptionsParams): Promise<StusdsRiskParameters> {
  async function fetchLockstakeLtv(): Promise<Percentage> {
    const skyIlk = await readContract(wagmiConfig, {
      address: getContractAddress(lockstakeEngineConfig.address, chainId),
      abi: lockstakeEngineConfig.abi,
      functionName: 'ilk',
      chainId,
    })
    const [, mat] = await readContract(wagmiConfig, {
      address: getContractAddress(spotterConfig.address, chainId),
      abi: spotterConfig.abi,
      functionName: 'ilks',
      args: [skyIlk],
      chainId,
    })

    return Percentage(RAY().div(bigNumberify(mat)))
  }

  async function fetchSkyMaxPriceCap(): Promise<NormalizedNumber> {
    const cap = await readContract(wagmiConfig, {
      address: getContractAddress(lockstakeCappedOsmWrapperConfig.address, chainId),
      abi: lockstakeCappedOsmWrapperConfig.abi,
      functionName: 'cap',
      chainId,
    })
    return BaseNumber(cap).toNormalizedUnit(18)
  }

  async function fetchLiquidationEnabled(): Promise<boolean> {
    const stopped = await readContract(wagmiConfig, {
      address: getContractAddress(lockstakeClipperConfig.address, chainId),
      abi: lockstakeClipperConfig.abi,
      functionName: 'stopped',
      chainId,
    })

    return stopped === 0n
  }

  const [lockstakeLtv, skyMaxPriceCap, liquidationEnabled] = await promiseAll(
    [fetchLockstakeLtv, fetchSkyMaxPriceCap, fetchLiquidationEnabled],
    { concurrency: 'unbounded' },
  )

  return {
    lockstakeLtv,
    skyMaxPriceCap,
    liquidationEnabled,
  }
}
